import { useState, useEffect, useRef } from 'preact/hooks';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { detectTimezone } from 'shared/dateutil';
import { showError } from '@components/app-error';
import { IcoMail, IcoUser } from '@components/icons';
import { BtnPrimary } from '@components/buttons';
import { FormGroup, AsyncForm } from '@components/async-form';
import { StripeContext, StripeInput } from '../checkout/stripe';
import { processStripePayment, PayWhatYouCanInput } from '../checkout/stripe-checkout-form';
import { describeCheckoutError, loggableCheckoutError } from '../checkout/checkout-logic';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';
import { CurrentUser, useAuth } from 'client/lib/auth';
import { CouponRow, PriceRow } from 'server/types';
import * as pmtmath from 'shared/payments/math';
import * as stripeutil from 'shared/payments/stripe';
import {
  CheckoutFn,
  PurchaseFn,
  StripeHandlerFn,
  AipBackupOfferDisplay,
} from 'shared/payments/types';
import { ERROR_CODES } from 'shared/consts';
import { WizardProgressBar } from './wizard-progress-bar';
import { TrustStrip } from '../checkout/trust-strip';
import { TaxBreakdown } from '../checkout/tax-breakdown';
import { COMPANY_NAME_FIELD_ID, SIMPLECHECK_CONSENT_PARAGRAPHS } from 'shared/mirasee-consts';

type Props = {
  courseId: string;
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
  actionText?: string;
  pricingNote?: string;
  pwywVariant?: 'slider' | 'box';
  onStepChange?: (step: number, totalSteps: number) => void;
  variant?: 'default' | 'campaign';
  testModeToken?: string;
  campaignSlug?: string;
  aipBackupOffer?: AipBackupOfferDisplay;
  /** Spell out the currency in the amount (US$10, CA$14): the page offers more than one. */
  explicitCurrency?: boolean;
  /** Show the SimpleCheck consent notice (campaign setting). */
  simpleCheckConsent?: boolean;
};

function SimpleCheckConsentNotice() {
  return (
    <div class="flex flex-col gap-2 text-xs leading-relaxed text-gray-500">
      {SIMPLECHECK_CONSENT_PARAGRAPHS.map((text) => (
        <p key={text}>{text}</p>
      ))}
    </div>
  );
}

function IconInputWrapper({
  icon,
  children,
}: {
  icon: preact.ComponentChildren;
  children: preact.ComponentChildren;
}) {
  return (
    <label class="relative flex">
      <span class="absolute flex items-center justify-center left-px top-px bottom-px w-10 bg-gray-50 rounded-l border-r">
        {icon}
      </span>
      {children}
    </label>
  );
}

export function CheckoutFormWizard({
  courseId,
  price,
  coupon,
  collectAddress,
  handleStripe,
  beginPurchase,
  onPurchaseComplete,
  actionText: customActionText,
  pricingNote,
  pwywVariant,
  onStepChange,
  variant = 'default',
  testModeToken,
  campaignSlug,
  aipBackupOffer,
  explicitCurrency,
  simpleCheckConsent,
}: Props) {
  // Phone, asked alongside the SimpleCheck notice and required there
  // (Lizzie, 2026-10-14). The server refuses the purchase without it too.
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const phoneLooksComplete = phone.replace(/\D/g, '').length >= 7;
  // True when the purchase may go ahead; otherwise shows why not.
  const requirePhone = () => {
    if (!simpleCheckConsent || phoneLooksComplete) {
      return true;
    }
    setPhoneError(intl('Please enter your phone number.'));
    return false;
  };
  // Every call below is a click on a form showing the notice, so it carries
  // the consent. (The auto-enroll on mount is skipped when the notice shows.)
  const purchase: PurchaseFn = (opts) =>
    beginPurchase(
      simpleCheckConsent
        ? { ...opts, simpleCheckConsent: true, phone: phone.trim() || undefined }
        : opts,
    );
  const intl = useIntl();
  const auth = useAuth();
  const currentUser = auth.user;
  // Campaign variant: used by IntegratedCheckout on campaign pages. Hides the
  // default WizardProgressBar and step titles (the parent renders its own), and
  // applies brand styling (colors/fonts come from CSS --m-* tokens via data-brand).
  const isCampaign = variant === 'campaign';
  const btnClass = isCampaign
    ? 'rounded-full px-12 py-3 font-bold text-base shadow-sm'
    : 'btn-primary rounded-full px-12 py-3 font-bold text-base bg-yellow-400 hover:bg-yellow-500 text-gray-900 border-yellow-400 shadow-sm';
  const btnStyle = isCampaign
    ? 'background-color: var(--m-yellow); border-color: var(--m-yellow); color: var(--m-btn-text); font-weight: 800; font-family: var(--m-font)'
    : undefined;
  const inputClass = isCampaign ? 'ruz-input campaign-input' : 'ruz-input';
  const phoneInput = (
    <div class="w-full flex flex-col gap-1">
      <input
        type="tel"
        name="phone"
        autoComplete="tel"
        required
        pattern="[+0-9\s\(\)\.\-]{7,20}"
        title={intl('Enter your full phone number.')}
        class={`${inputClass} w-full`}
        placeholder={intl('Phone')}
        value={phone}
        onInput={(e: any) => {
          setPhone(e.target.value);
          setPhoneError('');
        }}
      />
      {phoneError && <p class="text-sm text-red-600">{phoneError}</p>}
    </div>
  );

  // AIP repeat-trialer backup offer (no free trial; discounted first month).
  // Seeded from the getCampaignPage prop (logged-in load); the effect below
  // re-checks after an anonymous buyer authenticates mid-checkout.
  const [backupOffer, setBackupOffer] = useState(aipBackupOffer);
  const wasAnonAtMountRef = useRef(!currentUser);
  const backupCheckedRef = useRef(false);
  // An anonymous campaign buyer's backup offer is unknown until they authenticate
  // mid-checkout and we re-check (the effect below). Until that settles, the
  // Payment Element's mode could still flip (deferred=setup vs immediate=charge),
  // so we hold the Element mount + submit until it resolves — mounting in the
  // wrong mode would make confirmPayment run against a setup-mode Element. False
  // when no re-check will happen (logged-in load, non-campaign, or test mode).
  const [backupRecheckPending, setBackupRecheckPending] = useState(
    wasAnonAtMountRef.current && !!campaignSlug && !aipBackupOffer && !testModeToken,
  );

  const isFree = pmtmath.isFree({ price, coupon });
  // The backup offer keeps the price's free_trial_period so the Payment Element
  // stays in setup mode (card saved, charged on the deferred cohort date) — the
  // server, not the client, drops the trial + applies the $50-off coupon. The
  // one exception: a backup offer with NO deferred date charges immediately, so
  // the client must switch to charge mode to match the server.
  const backupChargesNow = !!backupOffer && !backupOffer.chargeDateLabel;
  const freeTrialPeriod = pmtmath.freeTrialPeriod({ price, coupon });
  const noChargeToday = !!freeTrialPeriod && !backupChargesNow;
  const paymentRequired =
    backupChargesNow || (!freeTrialPeriod && !!pmtmath.initialPrice({ price, coupon }));

  // Step count:
  //   Logged-in + free  → 0 steps (auto-enrolls immediately, no UI)
  //   Logged-in + paid  → 1 step  (payment only — name/email already known)
  //   Anonymous  + free  → 1 step  (info only — no payment needed)
  //   Anonymous  + paid  → 2 steps (info → payment)
  const loggedInSteps = isFree ? 0 : 1;
  const anonymousSteps = isFree ? 1 : 2;

  // Frozen at mount time — doesn't change when user authenticates during step 1
  const [useLoggedInFlow, setUseLoggedInFlow] = useState(!!currentUser);
  const totalSteps = useLoggedInFlow ? loggedInSteps : anonymousSteps;

  const steps =
    totalSteps === 2
      ? [
          { label: 'Your Info', icon: 'user' as const },
          { label: 'Payment', icon: 'payment' as const },
        ]
      : totalSteps === 1
        ? useLoggedInFlow
          ? [{ label: 'Payment', icon: 'payment' as const }]
          : [{ label: 'Your Info', icon: 'user' as const }]
        : [];

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep, totalSteps);
    }
  }, [currentStep, totalSteps]);

  const onPaymentStep = steps[currentStep]?.label === 'Payment';
  useEffect(() => {
    if (onPaymentStep && courseId) {
      rpx.campaignFunnel
        .recordCampaignFunnelStep({ courseId, eventType: 'payment_reached' })
        .catch(() => undefined);
    }
  }, [onPaymentStep, courseId]);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const [ctx, setCtx] = useState<StripeContext | undefined>(undefined);
  // The campaign currency switch swaps the price without remounting this
  // form. An amount chosen for one price must not carry into another, not
  // even for one render: that render mounts the new currency's Payment
  // Element with whatever amount it is given.
  const [amountChoice, setAmountChoice] = useState({
    priceId: price.id,
    cents: price.priceInCents,
  });
  const customAmount = amountChoice.priceId === price.id ? amountChoice.cents : price.priceInCents;
  function setCustomAmount(cents: number) {
    setAmountChoice({ priceId: price.id, cents });
  }
  // Guards against double-enrollment from rapid clicks or React re-renders
  const enrollingRef = useRef(false);
  // Tracks whether the user was created during this checkout (for password prompt on provision page).
  // Uses a ref because the value is read after page navigation, not during rendering.
  const isNewUserRef = useRef(!currentUser);
  // The authenticated user for the payment step. Set after step 1 authenticates the user.
  // Separate from auth.user because the wizard's flow shouldn't re-render when global auth changes.
  const [sessionUser, setSessionUser] = useState<NonNullable<CurrentUser> | undefined>(
    currentUser || undefined,
  );
  // A switch to another price brings that price's offer (the server withholds
  // it outside USD). Once the buyer has signed in, the reloaded prop already
  // reflects them; before that, re-check after they sign in. The old
  // currency's Stripe context and tax preview go too: the payment button waits
  // for the new Element, and the tax line for the refetch.
  const backupPriceIdRef = useRef(price.id);
  useEffect(() => {
    if (backupPriceIdRef.current === price.id) {
      return;
    }
    backupPriceIdRef.current = price.id;
    setBackupOffer(aipBackupOffer);
    backupCheckedRef.current = !!sessionUser;
    setCtx(undefined);
    setTaxPreview(undefined);
  }, [price.id]);
  // Re-check the backup offer once the buyer authenticates mid-checkout (only
  // for users anonymous at mount — a logged-in load already resolved it via
  // getCampaignPage). Resolves `backupRecheckPending` so the Payment Element
  // mounts once, in the correct mode.
  useEffect(() => {
    if (!sessionUser) {
      // Not authenticated yet (still on step 1) — can't re-check; keep waiting.
      return;
    }
    // Nothing to re-check (offer already known, already done, non-campaign,
    // logged-in at mount, or test mode): clear the gate so the Element mounts.
    if (
      backupOffer ||
      backupCheckedRef.current ||
      !campaignSlug ||
      !wasAnonAtMountRef.current ||
      testModeToken
    ) {
      setBackupRecheckPending(false);
      return;
    }
    backupCheckedRef.current = true;
    const checkedPriceId = price.id;
    rpx.courseCheckout
      .getAipBackupOfferStatus({ slug: campaignSlug, priceId: checkedPriceId })
      .then((result) => {
        if (backupPriceIdRef.current !== checkedPriceId) {
          return;
        }
        if (result.aipBackupOffer) {
          setBackupOffer(result.aipBackupOffer);
        }
      })
      .catch((err) => console.warn('aip backup re-check failed', err))
      .finally(() => setBackupRecheckPending(false));
  }, [sessionUser, campaignSlug, backupOffer, price.id, testModeToken]);
  const [taxPreview, setTaxPreview] = useState<
    { subtotal: number; taxAmount: number; total: number } | undefined
  >(undefined);
  const [taxLoading, setTaxLoading] = useState(false);
  const taxSeqRef = useRef(0);
  const lastAddressRef = useRef<{ country?: string; postal_code?: string; state?: string }>();
  const customAmountRef = useRef(customAmount);
  customAmountRef.current = customAmount;
  const taxTimerRef = useRef<ReturnType<typeof setTimeout>>();

  function doFetchTaxPreview(address: { country?: string; postal_code?: string; state?: string }) {
    if (!address.country || !address.postal_code) {
      return;
    }
    lastAddressRef.current = address;
    const seq = ++taxSeqRef.current;
    setTaxLoading(true);
    rpx.courseCheckout
      .previewTax({
        priceId: price.id,
        customAmountInCents: price.payWhatYouCan ? customAmountRef.current : undefined,
        couponId: coupon?.id,
        address: {
          country: address.country,
          postal_code: address.postal_code,
          state: address.state,
        },
      })
      .then((result) => {
        if (seq !== taxSeqRef.current) {
          return;
        }
        if (result.taxAmount > 0) {
          setTaxPreview(result);
        } else {
          setTaxPreview(undefined);
        }
      })
      .catch(() => {
        if (seq === taxSeqRef.current) {
          setTaxPreview(undefined);
        }
      })
      .finally(() => {
        if (seq === taxSeqRef.current) {
          setTaxLoading(false);
        }
      });
  }

  function fetchTaxPreview(address: { country?: string; postal_code?: string; state?: string }) {
    clearTimeout(taxTimerRef.current);
    taxTimerRef.current = setTimeout(() => doFetchTaxPreview(address), 300);
  }

  const name =
    useLoggedInFlow && currentUser ? currentUser.name : `${firstName} ${lastName}`.trim();

  // The backup offer replaces the free-trial CTA ("Start Free Trial") with a
  // discounted-first-month CTA, overriding the campaign-configured ctaText too.
  let actionText = backupOffer?.ctaText || customActionText;
  if (!actionText) {
    if (isFree) {
      actionText = intl("Register Now — It's Free! →");
    } else if (!paymentRequired && freeTrialPeriod) {
      actionText = intl('Start Free Trial →');
    } else {
      actionText = intl('Complete Purchase →');
    }
  }

  const unmountedRef = useRef(false);
  useEffect(() => {
    if (currentUser && useLoggedInFlow && isFree && !enrollingRef.current && !simpleCheckConsent) {
      enrollingRef.current = true;
      handleLoggedInFreeEnroll();
    }
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (lastAddressRef.current) {
      fetchTaxPreview(lastAddressRef.current);
    }
  }, [customAmount, coupon?.id, price.id]);

  const handleLoggedInFreeEnroll = async () => {
    if (!requirePhone()) {
      return;
    }
    try {
      setIsLoading(true);
      if (unmountedRef.current) {
        return;
      }
      await purchase({});
      if (unmountedRef.current) {
        return;
      }
      await onPurchaseComplete({ newUser: false });
    } catch (err) {
      enrollingRef.current = false;
      if (!unmountedRef.current) {
        showError(err);
      }
    } finally {
      if (!unmountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const switchToAnonymousFlow = () => {
    setUseLoggedInFlow(false);
    setSessionUser(undefined);
    isNewUserRef.current = true;
    setCurrentStep(0);
  };

  const authenticateUser = async (): Promise<{
    user: NonNullable<CurrentUser>;
    isNewUser: boolean;
  }> => {
    const result = await rpx.auth.loginOrRegisterForCheckout({
      courseId,
      name,
      email,
      timezone: detectTimezone(),
      testModeToken,
    });
    isNewUserRef.current = result.isNewUser;
    const user = { ...result.user, level: 'student' as const };
    // A checkout pass (an existing account's email, no sign-in) is not a
    // login: the server knows the buyer only for this purchase, so the client
    // must not treat them as signed in either.
    if (!result.checkoutPass) {
      await auth.login(Promise.resolve(user));
    }
    return { user, isNewUser: result.isNewUser };
  };

  async function saveCompanyName() {
    if (companyName.trim()) {
      try {
        await rpx.profileFields.saveStudentProfileFields({
          profileFields: [{ id: COMPANY_NAME_FIELD_ID, value: companyName.trim() }],
        });
      } catch {
        // Non-blocking — company name is optional
      }
    }
  }

  const handleStep1Submit = async () => {
    if (!requirePhone()) {
      return;
    }
    try {
      setIsLoading(true);
      setAccountError('');

      if (isFree) {
        const result = await authenticateUser();
        await saveCompanyName();
        await purchase({});
        await onPurchaseComplete({ newUser: result.isNewUser });
        return;
      }

      const result = await authenticateUser();
      await saveCompanyName();
      setSessionUser(result.user);

      setCurrentStep(totalSteps - 1);
    } catch (err: any) {
      if (err?.data?.code === ERROR_CODES.userExists) {
        setAccountError(
          'This email is associated with a staff account. Please use a different email address.',
        );
      } else if (err?.data?.errors?.[0]?.field === 'email') {
        setAccountError(
          "We couldn't verify this email address. Please double-check for typos and try again.",
        );
      } else {
        showError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    if (!ctx || !sessionUser || !requirePhone()) {
      return;
    }
    const pwycMin = Math.max(price.minPriceInCents, stripeutil.minPrice(price.currency));
    if (price.payWhatYouCan && customAmount < pwycMin) {
      return;
    }

    try {
      setIsLoading(true);
      setCardError(undefined);

      const checkout: CheckoutFn = async ({ priceId, couponId }) => {
        const result = await processStripePayment({
          ctx,
          user: sessionUser,
          priceId,
          couponId,
          processPayment: purchase,
          customAmountInCents: price.payWhatYouCan ? customAmount : undefined,
        });
        return !!result && result.type !== 'validationError';
      };

      const success = handleStripe
        ? await handleStripe({ checkout })
        : await checkout({ priceId: price.id, couponId: coupon?.id });

      if (!success) {
        return;
      }
      await onPurchaseComplete({ newUser: isNewUserRef.current });
    } catch (err) {
      const described = describeCheckoutError(err);
      reportCheckoutError({ step: 'checkout', priceId: price.id, err });
      if (described.kind === 'card') {
        setCardError(described.message);
        console.warn('checkout card error', loggableCheckoutError(err));
      } else {
        showError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const userFirstName = currentUser?.name?.split(' ')[0] || '';

  if (useLoggedInFlow && currentUser && isFree) {
    return (
      <div class="flex flex-col items-center gap-4 py-4">
        <div class="text-center">
          <p class="text-gray-800 font-semibold">
            {'👋'} Welcome{userFirstName ? ` ${userFirstName}` : ''}!
          </p>
          <p class="text-gray-500 text-sm">{currentUser.email}</p>
        </div>
        {simpleCheckConsent && (
          <>
            <div class="w-full max-w-sm">{phoneInput}</div>
            <SimpleCheckConsentNotice />
          </>
        )}
        <BtnPrimary
          class={btnClass}
          style={btnStyle}
          isLoading={isLoading}
          onClick={handleLoggedInFreeEnroll}
        >
          {actionText}
        </BtnPrimary>
        <button
          type="button"
          class="text-gray-500 text-sm hover:text-gray-700"
          onClick={switchToAnonymousFlow}
        >
          Not {userFirstName || currentUser.email}?
        </button>
        <TrustStrip />
      </div>
    );
  }

  const loggedInBanner = useLoggedInFlow && currentUser && (
    <div class="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-3 mb-4">
      <div>
        <p class="text-sm text-gray-800 font-semibold">
          {'👋'} Welcome{userFirstName ? ` ${userFirstName}` : ''}!
        </p>
        <p class="text-xs text-gray-500">{currentUser.email}</p>
      </div>
      <button
        type="button"
        class="text-blue-600 text-sm font-medium hover:text-blue-800"
        onClick={switchToAnonymousFlow}
      >
        Not {userFirstName || currentUser.email}?
      </button>
    </div>
  );

  const isPaymentStep = currentStep === totalSteps - 1 && !isFree;

  const isInfoStep = !useLoggedInFlow && currentStep === 0;

  const backupBanner = backupOffer && (
    <div class="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
      <span aria-hidden="true" class="text-lg leading-none">
        ⭐
      </span>
      <div class="flex flex-col gap-1">
        <p class="text-sm text-gray-900 font-semibold">{backupOffer.bannerHeading}</p>
        <p class="text-sm text-gray-700">{backupOffer.bannerText}</p>
      </div>
    </div>
  );

  return (
    <div>
      {totalSteps > 1 && !isCampaign && <WizardProgressBar steps={steps} current={currentStep} />}

      {backupBanner}

      {loggedInBanner}

      {loggedInBanner && simpleCheckConsent && (
        <div class="flex flex-col gap-4 mb-4">
          {phoneInput}
          <SimpleCheckConsentNotice />
        </div>
      )}

      {isInfoStep && (
        <div>
          <h3 class={`text-blue-700 font-bold text-lg mb-4 ${isCampaign ? 'hidden' : ''}`}>
            {totalSteps > 1 ? `1. ${steps[0].label}` : steps[0].label}
          </h3>
          <AsyncForm class="flex flex-col gap-4" onSubmit={handleStep1Submit}>
            <div class="grid grid-cols-2 gap-4">
              <FormGroup prop="firstName">
                <IconInputWrapper icon={<IcoUser />}>
                  <input
                    type="text"
                    name="firstName"
                    class={`${inputClass} pl-14`}
                    placeholder={intl('First Name')}
                    required
                    autoFocus={!isCampaign}
                    value={firstName}
                    onInput={(e: any) => setFirstName(e.target.value)}
                  />
                </IconInputWrapper>
              </FormGroup>
              <FormGroup prop="lastName">
                <input
                  type="text"
                  name="lastName"
                  class={inputClass}
                  placeholder={intl('Last Name')}
                  required
                  value={lastName}
                  onInput={(e: any) => setLastName(e.target.value)}
                />
              </FormGroup>
            </div>
            <FormGroup prop="email">
              <IconInputWrapper icon={<IcoMail />}>
                <input
                  type="email"
                  required
                  placeholder={intl('Email Address')}
                  name="email"
                  class={`${inputClass} block w-full sm:text-sm sm:leading-5 bg-transparent pl-14`}
                  data-private
                  value={email}
                  onInput={(e: any) => {
                    setEmail(e.target.value);
                    setAccountError('');
                  }}
                />
              </IconInputWrapper>
            </FormGroup>
            {simpleCheckConsent && <FormGroup prop="phone">{phoneInput}</FormGroup>}
            <FormGroup prop="companyName">
              <input
                type="text"
                name="companyName"
                class={inputClass}
                placeholder={intl('Company Name (optional)')}
                value={companyName}
                onInput={(e: any) => setCompanyName(e.target.value)}
              />
            </FormGroup>

            {simpleCheckConsent && <SimpleCheckConsentNotice />}

            {accountError && (
              <div class="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                {accountError}
              </div>
            )}

            <div class="flex flex-col items-center gap-3">
              <BtnPrimary class={btnClass} style={btnStyle} isLoading={isLoading}>
                {isFree || (!paymentRequired && freeTrialPeriod)
                  ? actionText
                  : intl('Continue to Payment →')}
              </BtnPrimary>
              <TrustStrip />
            </div>
          </AsyncForm>
        </div>
      )}

      {isPaymentStep && (
        <div>
          <h3 class={`text-blue-700 font-bold text-lg mb-4 ${isCampaign ? 'hidden' : ''}`}>
            {totalSteps}. Payment Details
          </h3>
          <div class="flex flex-col gap-4">
            {pricingNote && price.payWhatYouCan && (
              <div style="font-size: 16px; line-height: 1.45; color: #444; text-align: center">
                {pricingNote}
              </div>
            )}
            {price.payWhatYouCan && (
              <PayWhatYouCanInput
                key={price.id}
                price={price}
                value={customAmount}
                onChange={setCustomAmount}
                variant={pwywVariant}
                explicitCurrency={explicitCurrency}
              />
            )}
            {backupRecheckPending ? (
              <p class="text-gray-500 text-center text-sm py-6">
                {intl('Confirming your pricing…')}
              </p>
            ) : (
              <StripeInput
                customerName={name}
                priceId={price.id}
                onReady={setCtx}
                collectAddress={collectAddress}
                onAddressChange={fetchTaxPreview}
                disabled={isLoading}
                amount={
                  price.payWhatYouCan ? customAmount : pmtmath.initialPrice({ price, coupon })
                }
                currency={price.currency}
                mode={
                  noChargeToday
                    ? 'setup'
                    : price.paymentType === 'subscription' || price.paymentType === 'paymentplan'
                      ? 'subscription'
                      : 'payment'
                }
              />
            )}

            {cardError && (
              <div
                role="alert"
                class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {cardError}
              </div>
            )}

            <TaxBreakdown
              subtotal={taxPreview?.subtotal}
              taxAmount={taxPreview?.taxAmount}
              total={taxPreview?.total}
              currency={price.currency}
              isLoading={taxLoading}
            />

            {backupOffer ? (
              <p class="text-gray-600 text-center text-sm">
                {backupOffer.chargeDateLabel
                  ? `You'll be charged ${backupOffer.firstChargeLabel} on ${backupOffer.chargeDateLabel}, then ${backupOffer.recurringLabel}.`
                  : `You'll be charged ${backupOffer.firstChargeLabel} today, then ${backupOffer.recurringLabel}.`}
              </p>
            ) : (
              !paymentRequired && (
                <p class="text-gray-600 text-center text-sm">
                  {freeTrialPeriod
                    ? intl('You will not be charged until the end of the free trial period.')
                    : intl('You will not be charged today.')}
                </p>
              )
            )}

            <div class="flex flex-col items-center gap-3">
              <BtnPrimary
                class={btnClass}
                style={btnStyle}
                isLoading={isLoading || backupRecheckPending}
                onClick={handlePaymentSubmit}
              >
                {actionText}
              </BtnPrimary>
              {currentStep > 0 && (
                <button
                  type="button"
                  class="text-gray-500 text-sm hover:text-gray-700"
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  {intl('← Back')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
